import { BindingEngine } from 'aurelia-binding';
import { InstrumentValueGroup } from '@dynamik/common/clients/reporting-clients';
import { CustomerService, enumToExtendedArray, IApplicationFilter, PortfolioReportCache, property, widget, WidgetPropertyType } from '@dynamik/common';
import { WidgetBase } from '@dynamik/common';
import { instrumentValueFields } from '../instrument-value-fields';
import { Filter } from '../../../../types/filter';
import { customElement } from 'aurelia-templating';

export enum BreakdownPerDimensionWidgetGroupBy {
  Type = 1,
  Category = 2
}

export enum BreakdownPerDimensionWidgetOrderBy {
  TypeAscending = 1,
  TypeDescending = 2,
  ValueAscending = 3,
  ValueDescending = 4
}

declare module '@dynamik/common/clients/reporting-clients' {
  interface InstrumentValueGroup {
    percentage?: number;
  }
}

@customElement('breakdown-per-dimension-widget')
@widget('Breakdown per dimension', true, true, 'Description')
export class BreakdownPerDimensionWidget extends WidgetBase {
  constructor(element: Element, customerService: CustomerService, private portfolioReportCache: PortfolioReportCache, bindingEngine: BindingEngine) {
    super(element, bindingEngine, customerService);
  }

  BreakdownPerDimensionWidgetGroupBy = BreakdownPerDimensionWidgetGroupBy;
  instrumentGroups: InstrumentValueGroup[];
  categoryType: string;
  fieldName: string;
  totals: number;

  @property('Data', 'Aggregate', WidgetPropertyType.List, instrumentValueFields.map(x => ({ value: x.name, label: x.label })))
  aggregatedField: Filter<InstrumentValueGroup, number> = 'estimatedValue';

  @property('Data', undefined, WidgetPropertyType.List, enumToExtendedArray(BreakdownPerDimensionWidgetGroupBy))
  groupBy: BreakdownPerDimensionWidgetGroupBy = BreakdownPerDimensionWidgetGroupBy.Type;

  @property('Data', 'Category type', WidgetPropertyType.List)
  categoryTypeId: number | undefined;
  async categoryTypeIdListValues() {
    return this.customerService.instrumentCategories.filter(x => x.enabled).map(x => ({ value: x.catTypeId, label: x.catTypeName }));
  }

  @property('Data', undefined, WidgetPropertyType.List, enumToExtendedArray(BreakdownPerDimensionWidgetOrderBy))
  orderBy: BreakdownPerDimensionWidgetOrderBy = BreakdownPerDimensionWidgetOrderBy.TypeAscending;

  @property('Data')
  precision: number = 2;

  @property('Table')
  font?: string;

  @property('Table')
  fontSize?: number = 25;

  @property('Table')
  color?: string;

  @property()
  captionFont?: string;

  @property()
  captionFontSize?: number = 12;

  @property()
  captionColor?: string;

  override async loadData(filter: IApplicationFilter) {
    this.categoryType = this.groupBy === BreakdownPerDimensionWidgetGroupBy.Type
      ? 'Type'
      : this.customerService.instrumentCategories.find(x => x.catTypeId === this.categoryTypeId)?.catTypeName ?? 'Type';
    this.fieldName = instrumentValueFields.find(x => x.name === this.aggregatedField).label;
    const categoryTypeId = this.groupBy === BreakdownPerDimensionWidgetGroupBy.Category ? this.categoryTypeId : undefined;
    const data = await this.portfolioReportCache.instrumentValueGroupsOverTime(filter.customer.companyId, categoryTypeId, filter.dates.fromStr, filter.dates.toStr, false);
    return data.filter(x => x.date.startsWith(filter.dates.toStr));
  }

  override async setData(data: InstrumentValueGroup[]) {
    switch (this.orderBy) {
      case BreakdownPerDimensionWidgetOrderBy.TypeAscending:
        this.instrumentGroups = data.sort((a, b) => a.group.localeCompare(b.group));
        break;
      case BreakdownPerDimensionWidgetOrderBy.TypeDescending:
        this.instrumentGroups = data.sort((a, b) => b.group.localeCompare(a.group));
        break;
      case BreakdownPerDimensionWidgetOrderBy.ValueAscending:
        this.instrumentGroups = data.sort((a, b) => a[this.aggregatedField] - b[this.aggregatedField]);
        break;
      case BreakdownPerDimensionWidgetOrderBy.ValueDescending:
        this.instrumentGroups = data.sort((a, b) => b[this.aggregatedField] - a[this.aggregatedField]);
        break;
    }
    this.totals = data.reduce((p, c) => p + c[this.aggregatedField], 0);
    data.forEach(x => x.percentage = x[this.aggregatedField] / this.totals * 100);

    // Populate excelData for export
    this.excelData = {
      fileName: 'BreakdownPerDimension',
      data: {
        Items: this.instrumentGroups.map(item => ({
          Group: item.group,
          Value: item[this.aggregatedField],
          Percentage: item.percentage
        }))
      }
    };
  }

  override configChanged(key: keyof BreakdownPerDimensionWidget) {
    switch (key) {
      case 'font':
      case 'fontSize':
      case 'color':
      case 'captionFont':
      case 'captionFontSize':
      case 'captionColor':
      case 'precision':
        break;
      default:
        this.refresh$.next(undefined);
        break;
    }
  }
}
